'use client'

import type { ReactNode } from 'react'
import type { Address } from 'viem'

import { buttonVariants, cn, CopyButton, Text } from '@repo/ui'
import { ArrowDiagonalUp } from '@repo/ui/icons'
import { abbreviateAddress } from '@repo/utils'

import {
  EntityHoverCard,
  EntityHoverCardContent,
  EntityHoverCardDivider,
  EntityHoverCardRow,
  EntityHoverCardRows,
  EntityHoverCardTrigger
} from './EntityHoverCard'
import { EtherscanLink } from './EtherscanLink'

interface OracleHoverCardProps {
  chainId: number
  oracleAddress: Address
  loanToken: { address?: Address; symbol: string }
  collateralToken: { address?: Address; symbol: string }
  /** Pre-formatted "1 X = N Y" string. Omit to hide the row. */
  exchangeRate?: string
  /**
   * Off when the trigger already renders its own contained hover state, e.g. a Badge in
   * a tight row of pills, where the padded highlight would overlap neighbours.
   */
  highlightOnHover?: boolean
  /**
   * Optional slot rendered below the static rows, separated by a divider.
   * Hooks inside only run on first hover open since the popover unmounts
   * its content while closed.
   */
  metadataContent?: ReactNode
  children: ReactNode
}

export function OracleHoverCard({
  chainId,
  oracleAddress,
  loanToken,
  collateralToken,
  exchangeRate,
  highlightOnHover = true,
  metadataContent,
  children
}: OracleHoverCardProps) {
  const pair = `${collateralToken.symbol} / ${loanToken.symbol}`
  const abbreviated = abbreviateAddress(oracleAddress, 4)

  return (
    <EntityHoverCard>
      <EntityHoverCardTrigger asChild>
        <div
          tabIndex={0}
          aria-label={`Oracle ${abbreviated} details`}
          className={cn(
            'focus-visible:ring-ring/50 relative inline-flex w-fit cursor-help items-center outline-none focus-visible:ring-[3px]',
            highlightOnHover &&
              'hover:before:bg-muted data-[state=open]:before:bg-muted before:pointer-events-none before:absolute before:-inset-x-2 before:-inset-y-1.5 before:rounded-md before:transition-colors'
          )}
        >
          {children}
        </div>
      </EntityHoverCardTrigger>
      <EntityHoverCardContent className="w-80">
        <EntityHoverCardRows aria-label={`Oracle details for ${pair}`}>
          <EntityHoverCardRow label="Oracle">
            <div className="-mr-2.5 flex items-center gap-0.5">
              <CopyButton
                value={oracleAddress}
                variant="ghost"
                size="sm"
                className="text-foreground text-xs"
                successMessage="Copied oracle address to clipboard"
                label={`Copy oracle address ${abbreviated}`}
              >
                {abbreviated}
              </CopyButton>
              <EtherscanLink
                chainId={chainId}
                address={oracleAddress}
                className={buttonVariants({ variant: 'ghost', size: 'iconSm' })}
                aria-label={`Open oracle ${abbreviated} on explorer`}
              >
                <ArrowDiagonalUp className="text-muted-foreground size-5" />
              </EtherscanLink>
            </div>
          </EntityHoverCardRow>

          {exchangeRate && (
            <EntityHoverCardRow label="Oracle price">
              <Text size="xs">{exchangeRate}</Text>
            </EntityHoverCardRow>
          )}
        </EntityHoverCardRows>

        {metadataContent && (
          <>
            <EntityHoverCardDivider />
            {metadataContent}
          </>
        )}
      </EntityHoverCardContent>
    </EntityHoverCard>
  )
}
